import { useCallback, useMemo } from 'react'
import { PRMarinaFileArtboard } from '@sketch-hq/sketch-web-renderer'
import { throttle } from 'lodash'

import {
  ArtboardOverlayProps,
  TouchableClickEvent,
} from '../../../../canvas/components/ArtboardOverlay'
import { useStableHandler } from '@sketch/utils'
import {
  useInspectorHoveredElement,
  useInspectorContext,
} from '../../../FrameView'
import { findSketchElementFromUuid } from '../../../FrameView/hooks/inspector/sketchSceneLib'

/**
 * Hook that returns props specific to inspect mode for the ArtboardOverlay component.
 */
export function useInspectModeOverlayProps(
  frame: PRMarinaFileArtboard
): Partial<ArtboardOverlayProps> {
  const {
    selectedElement,
    setSelectedElement,
    setHoveredElement,
    sketchSceneRootElement,
  } = useInspectorContext()
  const hoveredElement = useInspectorHoveredElement()

  const isSelected = selectedElement?.elementUUID === frame.objectId
  const isHovered = hoveredElement?.elementUUID === frame.objectId

  const setSelectedTopLevelFrame = useCallback(
    (frameUuid: string) => {
      const frameElement = findSketchElementFromUuid(
        sketchSceneRootElement,
        frameUuid
      )
      if (frameElement) {
        setSelectedElement(frameElement)
      }
    },
    [setSelectedElement, sketchSceneRootElement]
  )

  const setHoveredTopLevelFrame = useCallback(
    (frameUuid: string) => {
      const frameElement = findSketchElementFromUuid(
        sketchSceneRootElement,
        frameUuid
      )
      if (frameElement) {
        setHoveredElement(frameElement)
      }
    },
    [setHoveredElement, sketchSceneRootElement]
  )

  const handleSelectFrame = useStableHandler(
    (e: TouchableClickEvent<HTMLDivElement>) => {
      // Prevents the click from propagating to the canvas which would
      // remove the selection just after we set it.
      e.stopPropagation()
      if (!isSelected) {
        setSelectedTopLevelFrame(frame.objectId)
      }
    }
  )

  // Throttle setHoveredTopLevelFrame as we can receive many mouse move events
  const setHoveredTopLevelFrameThrottled = useMemo(
    () => throttle(setHoveredTopLevelFrame, 50),
    [setHoveredTopLevelFrame]
  )

  // Note: We are using MouseMove instead of MouseEnter/MouseLeave
  // to avoid the hover state being removed by the useInspectorOverlayEvents.
  // We still rely on the useInspectorOverlayEvents to automatically remove the hover state
  // when the mouse leaves the frame header.
  const handleHeaderMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Prevent listener in useInspectorOverlayEvents from removing the hover state
      // just after we set it.
      e.stopPropagation()

      // Only set hover if this frame is not already hovered to avoid unnecessary updates
      if (!isHovered) {
        setHoveredTopLevelFrameThrottled(frame.objectId)
      }
    },
    [frame.objectId, isHovered, setHoveredTopLevelFrameThrottled]
  )

  return {
    disableAutoHover: true,
    // Note: We are setting the handler to undefined when not needed as having a value
    // or not controls if the cursor is a pointer or not in the FrameOverlay component.
    onFrameHeaderClick: !isSelected ? handleSelectFrame : undefined,
    onFrameHeaderMouseMove: handleHeaderMouseMove,
  }
}
